import { Component, EventEmitter, HostListener, Input, OnInit, Output, OnDestroy } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { BehaviorSubject, Subscription } from 'rxjs';
import { UserService } from '../services/user.service';
import { AppText, disabledLoginTypes } from '../interfaces/common';
import { DisplayService } from '../services/display.service';

@Component({
  selector: 'app-login-prompt',
  templateUrl: './login-prompt.component.html',
  styleUrls: ['./login-prompt.component.css']
})

export class LoginPromptComponent implements OnInit, OnDestroy {
  @Input() selText: AppText = {};
  @Input() applicationSettings: any;
  @Output() groupsUserIsInEmitter = new EventEmitter<string[]>();

  username: UntypedFormControl = new UntypedFormControl("", [(control) => Validators.required(control)])
  password: UntypedFormControl = new UntypedFormControl("", [(control) => Validators.required(control)])
  failedLogin: boolean = false
  isUserLoggedInSub: Subscription | null = null;
  disabledLoginTypes: disabledLoginTypes | null = null;
  islandLoginAllowed: boolean = true;
  googleLoginAllowed: boolean = true;
  lmiLoginAllowed: boolean = true;
  passwordFieldType: string = 'password';
  loginRequired: boolean = false;

  //Pressing Enter should try to login via local-LMÍ login.
  @HostListener("document:keypress", ["$event"])
  onKeyPressed(event: KeyboardEvent) {
    if (event.key === "Enter") { this.loginWithLMI() }
  }

  constructor(private userService: UserService, private displayService: DisplayService) { }

  ngOnInit(): void {
    this.disabledLoginTypes = this.displayService.getDisabledLoginTypes()

    if (this.disabledLoginTypes) {
      if (this.disabledLoginTypes.includes("island")) this.islandLoginAllowed = false
      if (this.disabledLoginTypes.includes("google")) this.googleLoginAllowed = false
      if (this.disabledLoginTypes.includes("lmi")) this.lmiLoginAllowed = false
    }
    
    const isUserLoggedIn$: BehaviorSubject<boolean | null> = this.userService.getIsUserLoggedIn();
    
    this.isUserLoggedInSub = isUserLoggedIn$.subscribe((isLoggedIn: boolean | null) => {
      //if login failed -> clear the old values from the fields and display wrong username/password message
      //the untouched must be false is to prevent the message from showing when user logged in succesfully but is not allowed in and is returned to login-prompt
      if (isLoggedIn === false && this.username.untouched == false && this.password.untouched == false) {
        //this.username.setValue("")
        //this.password.setValue("")
        this.failedLogin = true 
      }
    })
    
    //Used to remove the failed login status message when user starts typing again a new username/password
    this.username.statusChanges.subscribe(() => { if (this.failedLogin) this.failedLogin = false });
    this.password.statusChanges.subscribe(() => { if (this.failedLogin) this.failedLogin = false });

    if (this.applicationSettings?.option?.allowedGroups && !this.applicationSettings?.option?.allowReadAccess) {
      this.loginRequired = true;
    }
  }

  ngOnDestroy(): void {
    this.isUserLoggedInSub?.unsubscribe()
  }

  togglePasswordVisibility(event: PointerEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password';
  }

  /*
    LMI Login flow:
      Login failed(wrong username/password) -> http login-call in user service wil return error, sends false into behaviourSubject
      isUserLoggedIn, false received in subscription here in login-prompt, Shows message saying wrong username/password.
      
      login success -> http login call in user service goes through, send true into behaviourSubject isUserLoggedIn,
      true received in subscription in login-prompt, another http call is immideatly performed to get the groups the current
      user is in, when groups arrive they are emitted up to app.component to the function isUserInRightGroup(...), this
      function is run to determine if user is allowed or not into the application.

    V2 LMI Login flow:
      Login failed(wrong username/password) -> http login-call in user service wil return valid response with key error: "username or password invalid",
      we send false into behaviourSubject isUserLoggedIn, false received in subscription here in login-prompt, Shows message saying wrong username/password.
    
      Login success -> http login call in user service goes through, send true into behaviourSubject isUserLoggedIn and fill in the received data in user.service. 
      That same BehaviourSubject recieves true in the subscription in app.component. When it does it runs isUserInRightGroup() and checks if user
      is allowed into this application.
  */
  loginWithLMI(): void {
    if (!this.lmiLoginAllowed) return
    //Return out early if any fo the fields is invalid (They are invalid if they are empty)
    if (this.username.status !== "VALID" || this.password.status !== "VALID") {
      //console.warn("Password or username is not valid")
      return;
    }
    this.userService.login({
      'username': this.username.value,
      'password': this.password.value,
    });

  }
  /*
    Google login flow:
      Login failed -> Google handles it (:
      
      Login success -> User is redirected back to mapview after logging in at googles login page.
      mapview starts at the top again but now the getUserInfo() in app-load.service receives all the User
      information and the usual "user is logged in"-flow gets executed. (Checking groups if this is such application and so on...)
  */
  loginWithGoogle(): void {
    if (!this.googleLoginAllowed) return

    this.userService.getThirdPartyLogin("google").subscribe((data: {rc: {Location: string}}) => {
      this.openThirdPartyLogin(data)
    })
  }

  loginWithIsland(): void {
    if (!this.islandLoginAllowed) return;

    this.userService.getThirdPartyLogin("island").subscribe((data: {rc: {Location: string}}) => {
      this.openThirdPartyLogin(data);
    })
  }

  /**
   * Checks that received login URL object is valid opens it in the same window
   * @param data Object with login link
   * @returns 
   */
  private openThirdPartyLogin(data: {rc: {Location: string}}): void {
    if (!data?.rc?.Location) {
      console.warn("The login URL received was invalid. It was: " + data?.rc?.Location)
      return
    }
    window.open(data.rc.Location, "_self")
  }

  cancelLogin(): void {
    window.location.reload();
  }
}